import React, { Fragment } from 'react';
import ReactFocusLock from 'react-focus-lock';
import { useHydrated } from '../../hooks/use-hydrated';

/**
 * A component that renders its children only after the client has hydrated.
 *
 * @param {ReactNode} props.children - The children to render once hydrated.
 * @returns {ReactNode | null} The children if hydrated, otherwise null.
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const ClientOnly = ({
  children
}) => {
  const isHydrated = useHydrated();
  return isHydrated ? children : null;
};
// Include all props for the wrapper component

/**
 * A wrapper component that conditionally renders its children wrapped in component
 * during client side hydration. During server-side rendering, the children are rendered without the wrapper.
 *
 * @template T - The type of the props for the client component.
 * @param {ComponentType<T>} props.clientComponent - The client-side component to render when hydrated.
 * @param {ReactNode} props.children - The children to render inside the wrapper.
 * @param {T} [props.wrapperProps] - Additional props to pass to the client component.
 * @returns {ReactNode} - The rendered component based on the hydration state.
 */
const ClientOnlyWrapper = ({
  clientComponent: ClientComponent,
  children,
  ...wrapperProps
}) => {
  const isHydrated = useHydrated();

  // If not hydrated, render just the children inside a Fragment
  if (!isHydrated) {
    return /*#__PURE__*/_jsx(Fragment, {
      children: children
    });
  }

  // If hydrated, render the ClientComponent with its children and props
  return /*#__PURE__*/_jsx(ClientComponent, {
    ...wrapperProps,
    children: children
  });
};

// A wrapper for ReactFocusLock that only renders the component after hydration
export const RemixFocusLock = ({
  children,
  ...rest
}) => /*#__PURE__*/_jsx(ClientOnlyWrapper, {
  clientComponent: ReactFocusLock,
  ...rest,
  children: children
});

// ... additional ClientOnlyWrapper components for remix if needed